import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useUpdateSearchParam } from '@shared/utils/updateSearchParam';
import { useWebsiteEditorDialogContext } from '@website-editor/context/WebsiteEditorDialogContext';
import { ACCOMMODATIONS_DIALOGS } from '../../Accommodations.dialogs';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { DialogReadiness } from '@shared/hooks/useDialogManager';
import { useMount } from '@shared/utils/hooks/useMount';
import { useAutoPopulation, useHasSeenOnboardingVenueDialog, useIsAccommodationsTourEnabled } from '@shared/utils/accommodations';

const VENUE_KEY = 'venue';

export enum EventVenueState {
  create = 'create',
  update = 'update',
  error = 'error',
  intro = 'intro',
  filters = 'filters',
  hotels = 'hotels',
  noHotels = 'noHotels',
  confirm = 'confirm',
}

export const useEventVenueDialog = ({ hasAccommodations, loadingData }: { hasAccommodations: boolean; loadingData?: boolean }) => {
  const [eventVenueState, setEventVenueState] = useState<EventVenueState>(EventVenueState.create);
  const hasMarkedAsHideRef = useRef(false);
  const isDashboardRedirectRef = useRef(false);

  const { isAccommodationsTourEnabled, isLoading: isLoadingIsAccommodationsTourEnabled } = useIsAccommodationsTourEnabled();

  const updateSearchParam = useUpdateSearchParam();
  const queryParamHelper = useQueryParamHelper();
  const venueKey = queryParamHelper.getValueString(VENUE_KEY);
  const { registerDialog, dismiss, isOpen, requestShow } = useWebsiteEditorDialogContext(ACCOMMODATIONS_DIALOGS.VENUE_DIALOG);

  const { status: autoPopulationStatus, results: autoPopulationResults } = useAutoPopulation({ mode: 'poll-only', context: 'useEventVenueDialog' });

  const { hasSeenOnboardingVenueDialog, setHasSeenOnboardingVenueDialog, isLoadingOnboardingVenueDialogFlag } = useHasSeenOnboardingVenueDialog();

  const { venueInfo, loading } = useEventPlaces();
  const isRedirectFromDashboard = isDashboardRedirectRef.current;

  const shouldShowVenueDialog = useMemo(() => {
    if (isAccommodationsTourEnabled) {
      // New onboarding: only show if redirected from dashboard click on "Venue Menu" and haven't seen the dialog
      return isRedirectFromDashboard && !hasSeenOnboardingVenueDialog;
    } else {
      // Existing onboarding: show if redirected from dashboard click on "Venue Menu" OR "Where to Stay" CTA and haven't seen the dialog
      return isRedirectFromDashboard || !hasSeenOnboardingVenueDialog;
    }
  }, [isAccommodationsTourEnabled, isRedirectFromDashboard, hasSeenOnboardingVenueDialog]);

  // Capture dashboard redirect before clearing URL param
  useMount(() => {
    if (venueKey === 'true') {
      isDashboardRedirectRef.current = true;
      updateSearchParam(VENUE_KEY, null, true);
    }
  });

  // Calculate dialog readiness
  const readiness = useMemo((): DialogReadiness => {
    if (loadingData || loading || isLoadingOnboardingVenueDialogFlag || isLoadingIsAccommodationsTourEnabled) return 'loading';

    const hasVenue = venueInfo?.hasVenue;
    // A. No venue saved. click on venue on dashboard -> Redirect to "Where to Stay" -> See "Add Venue" modal -> Start Autopopulate
    // B. No venue saved. Navigate to "Where to Stay" -> See "Add Venue" modal -> Start Autopopulate
    // No venue, first time show add venue dialog.
    if (!hasVenue && shouldShowVenueDialog && autoPopulationStatus === 'COMPLETED') {
      if (!hasMarkedAsHideRef.current) {
        hasMarkedAsHideRef.current = true;
        setHasSeenOnboardingVenueDialog();
        setEventVenueState(EventVenueState.create);
      }
      return 'ready';
    }

    // C. Have venue. Navigate to "Where to Stay" --> (Have not Autopopulate) -> See "Confirm Venue" modal(Only first time visit) -> Start Autopopulate(User could opt-out from intro flow)
    if (hasVenue && !hasAccommodations && shouldShowVenueDialog && autoPopulationStatus === 'COMPLETED' && autoPopulationResults.length === 0) {
      if (!hasMarkedAsHideRef.current) {
        hasMarkedAsHideRef.current = true;
        setHasSeenOnboardingVenueDialog();
        setEventVenueState(EventVenueState.confirm);
      }
      return 'ready';
    }

    // E. Have venue. Click on Venue in Dashboard -> "Update Venue"
    if (hasVenue && isDashboardRedirectRef.current && autoPopulationStatus === 'COMPLETED') {
      if (!hasMarkedAsHideRef.current) {
        hasMarkedAsHideRef.current = true;
        setHasSeenOnboardingVenueDialog();
        setEventVenueState(EventVenueState.update);
      }
      return 'ready';
    }

    // Fallback to update or create state, if skipped from other conditions
    setEventVenueState(hasVenue ? EventVenueState.update : EventVenueState.create);

    return 'skip';
  }, [
    loadingData,
    loading,
    autoPopulationStatus,
    venueInfo?.hasVenue,
    autoPopulationResults.length,
    hasAccommodations,
    setHasSeenOnboardingVenueDialog,
    isLoadingOnboardingVenueDialogFlag,
    isLoadingIsAccommodationsTourEnabled,
    shouldShowVenueDialog,
  ]);

  // Register with dialog manager
  useEffect(() => {
    registerDialog(readiness);
  }, [registerDialog, readiness]);

  const handleClose = useEventCallback(() => {
    dismiss();
  });

  const requestOpen = useEventCallback(() => {
    requestShow();
  });

  const dialogIsOpen = isOpen();

  return useMemo(
    () => ({
      venueInfo,
      eventVenueState,
      setEventVenueState,
      isOpen: dialogIsOpen,
      onClose: handleClose,
      requestOpen,
    }),
    [venueInfo, eventVenueState, setEventVenueState, dialogIsOpen, handleClose, requestOpen],
  );
};
